import { getLogger } from '@packages/core/logging';
import {
    type AssetCompletionCheckResult,
    CheckForCompletion,
    determineAssetType,
    filterUploadingAssets,
    MarkErrored,
    type TechlineAssetEntity,
    type TechlineAssetMediaItem,
    UploadAsset,
} from '@packages/domain/techline/assets';
import { container } from '@web/services/container';
import { useCallback, useRef, useState } from 'react';

const logger = getLogger('techline');

// Operations are prefixed with $ to make them visually distinct in this file.
const [$checkForCompletion, $markErrored, $uploadAsset] = container.resolveOperations(
    CheckForCompletion,
    MarkErrored,
    UploadAsset
);

/** Poll interval in milliseconds between asset completion checks. */
const POLL_INTERVAL_MS = 5000;

/** Maximum number of poll iterations before marking assets as errored. */
const MAX_POLL_RETRIES = 10;

/** Prefix for temporary asset IDs generated before the server assigns a real one. */
const TEMP_ID_PREFIX = 'temp:';

/**
 * Orchestrate the full lifecycle of uploading a file as a techline case asset.
 * Bridges the gap between "file selected" and "asset fully processed by server"
 * by maintaining in-progress asset entities that cover every lifecycle phase:
 * uploading (XHR in progress), processing (server-side Lambda), complete, or error.
 *
 * This hook eliminates the flicker that occurs when a local upload placeholder
 * is cleared before the server finishes processing the asset.
 *
 * @param onAssetSettled - callback invoked when an asset reaches 'complete' or 'error';
 *   the consumer (useAssets) uses this to refresh server data
 * @param onAssetError - callback invoked when an asset fails during upload or processing;
 *   receives the filename and error message so the consumer can display a notification
 * @returns in-progress asset data and upload function
 */
export function useAssetUpload(
    onAssetSettled: () => Promise<void>,
    onAssetError: (filename: string, error: string) => void
) {
    const [inProgressAssets, setInProgressAssets] = useState<Record<string, TechlineAssetEntity>>({});
    const inProgressAssetsRef = useRef(inProgressAssets);
    inProgressAssetsRef.current = inProgressAssets;

    const pollTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

    /**
     * Update a single in-progress asset's fields.
     *
     * @param id - the asset ID to update
     * @param updates - partial entity fields to merge
     */
    const updateAsset = useCallback((id: string, updates: Partial<TechlineAssetEntity>) => {
        setInProgressAssets((prev) => {
            if (!prev[id]) {
                return prev;
            }
            return { ...prev, [id]: { ...prev[id], ...updates } };
        });
    }, []);

    /**
     * Replace an in-progress asset's key (temp ID -> server ID) and merge updates.
     *
     * @param oldId - the temporary ID to replace
     * @param newId - the server-assigned asset ID
     * @param updates - partial entity fields to merge
     */
    const replaceAssetId = useCallback((oldId: string, newId: string, updates: Partial<TechlineAssetEntity>) => {
        setInProgressAssets((prev) => {
            const entry = prev[oldId];
            if (!entry) {
                return prev;
            }
            const { [oldId]: _, ...rest } = prev;
            return { ...rest, [newId]: { ...entry, ...updates, id: newId } };
        });
    }, []);

    /**
     * Remove an in-progress asset by ID. Revokes any blob URLs in the
     * asset's media array to prevent memory leaks.
     *
     * @param id - the asset ID to remove
     */
    const removeAsset = useCallback((id: string) => {
        setInProgressAssets((prev) => {
            const entry = prev[id];
            if (!entry) {
                return prev;
            }

            // Revoke any blob URLs to free memory
            for (const media of entry.media) {
                if (media.url.startsWith('blob:')) {
                    URL.revokeObjectURL(media.url);
                }
            }

            const { [id]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    /**
     * Poll a single asset for completion, then settle it (refresh server data + cleanup).
     *
     * @param assetId - the server-assigned asset ID to poll
     * @param retryCount - current retry iteration
     */
    const pollAsset = useCallback(
        (assetId: string, retryCount: number) => {
            pollTimersRef.current[assetId] = setTimeout(async () => {
                const result: AssetCompletionCheckResult = await $checkForCompletion({
                    uploadingAssetIds: [assetId],
                });

                const stillUploading = filterUploadingAssets(result.stillUploading);

                if (result.settled.length) {
                    // Asset finished processing
                    const settled = result.settled[0];
                    updateAsset(assetId, { status: settled.status });

                    // Refresh server data so the server asset replaces the in-progress entry
                    await onAssetSettled();

                    if (settled.status === 'error') {
                        // Notify the consumer so it can display an AlertBar
                        const name = inProgressAssetsRef.current[assetId]?.name ?? 'Unknown file';
                        onAssetError(name, 'Processing failed');
                        removeAsset(assetId);
                    } else {
                        // Remove the in-progress entry (server data now has the real asset)
                        removeAsset(assetId);
                    }
                } else if (stillUploading.length && retryCount < MAX_POLL_RETRIES) {
                    // Still processing; try again
                    pollAsset(assetId, retryCount + 1);
                } else if (stillUploading.length) {
                    // Exceeded retry threshold; mark as errored on the server and notify consumer
                    logger.warn('Upload processing timed out, marking the asset as errored', { assetId, retryCount });
                    await $markErrored({ assets: stillUploading });
                    const name = inProgressAssetsRef.current[assetId]?.name ?? 'Unknown file';
                    onAssetError(name, 'Processing of upload taking longer than normal');
                    removeAsset(assetId);
                }

                // Clean up timer reference
                delete pollTimersRef.current[assetId];
            }, POLL_INTERVAL_MS);
        },
        [onAssetError, onAssetSettled, removeAsset, updateAsset]
    );

    /**
     * Upload a file as a techline case asset. Creates a local placeholder entity,
     * uploads the file via the UploadAsset operation, then polls for server-side
     * processing completion.
     *
     * @param file - the file to upload
     * @param caseNumber - the case this asset belongs to
     * @param caseStatusCode - the current case status code
     */
    const uploadAsset = useCallback(
        async (file: File, caseNumber: string, caseStatusCode: string) => {
            // Generate a temporary ID for the placeholder
            const tempId = `${TEMP_ID_PREFIX}${Date.now()}-${file.name}`;

            // Determine asset type from MIME type
            const assetType = determineAssetType(file.type);

            // Create the placeholder entity
            const placeholder: TechlineAssetEntity = {
                assetType,
                contentType: file.type,
                createdAt: new Date().toISOString(),
                disposition: 'TechShare',
                downloadUrl: '',
                hasOverlay: false,
                id: tempId,
                isImportedFromSiebel: false,
                isWorksheetAsset: false,
                media: [],
                name: file.name,
                note: '',
                progress: 0,
                status: 'uploading',
                worksheetSourceId: '',
            };

            // Add the placeholder to in-progress state
            setInProgressAssets((prev) => ({ ...prev, [tempId]: placeholder }));

            try {
                // Upload the file and get the server-assigned asset ID
                const assetId = await $uploadAsset({
                    caseNumber,
                    caseStatusCode,
                    file,
                    onProgress: (percent: number) => {
                        updateAsset(tempId, { progress: percent });
                    },
                });

                // Replace temp ID with real asset ID and transition to processing
                replaceAssetId(tempId, assetId, { progress: 100, status: 'processing' });

                // Begin polling for server-side processing completion
                pollAsset(assetId, 0);
            } catch {
                // Upload failed; notify consumer and remove the placeholder immediately
                onAssetError(file.name, 'Upload failed');
                removeAsset(tempId);
            }
        },
        [onAssetError, pollAsset, removeAsset, replaceAssetId, updateAsset]
    );

    /**
     * Track a replacement asset (from an edit/save) in the polling loop.
     * The asset already exists server-side in UPLOADING status; this creates
     * a local "processing" placeholder and polls until the asset settles.
     * When a preview blob is provided, a blob URL is created and used as the
     * thumbnail so the user sees the edited image immediately during processing.
     *
     * @param assetId - the server-assigned ID of the replacement asset
     * @param name - the display name of the asset
     * @param assetType - the type of the asset (Image, Video, File)
     * @param previewBlob - optional blob from the editor to use as an immediate thumbnail preview
     */
    const trackReplacementAsset = useCallback(
        (assetId: string, name: string, assetType: TechlineAssetEntity['assetType'], previewBlob?: Blob) => {
            // Build the media array with a blob URL thumbnail when a preview is available
            const media: TechlineAssetMediaItem[] = [];
            if (previewBlob) {
                const blobUrl = URL.createObjectURL(previewBlob);
                media.push({
                    assetImageTypeId: 'THUMBNAIL',
                    contentType: previewBlob.type,
                    createdTimestamp: new Date().toISOString(),
                    createdTimestampDescription: '',
                    sizeInBytes: previewBlob.size,
                    sizeInBytesDescription: '',
                    url: blobUrl,
                });
            }

            // Create a processing placeholder for the replacement asset
            const placeholder: TechlineAssetEntity = {
                assetType,
                contentType: '',
                createdAt: new Date().toISOString(),
                disposition: 'TechShare',
                downloadUrl: '',
                hasOverlay: false,
                id: assetId,
                isImportedFromSiebel: false,
                isWorksheetAsset: false,
                media,
                name,
                note: '',
                progress: 100,
                status: 'processing',
                thumbnailUrl: media?.find((m) => m.assetImageTypeId === 'THUMBNAIL')?.url,
                worksheetSourceId: '',
            };

            // Add the placeholder to in-progress state
            setInProgressAssets((prev) => ({ ...prev, [assetId]: placeholder }));

            // Begin polling for server-side processing completion
            pollAsset(assetId, 0);
        },
        [pollAsset]
    );

    /**
     * Clean up all active poll timers. Should be called on unmount.
     */
    const clearAllPolling = useCallback(() => {
        Object.values(pollTimersRef.current).forEach(clearTimeout);
        pollTimersRef.current = {};
    }, []);

    return {
        /** Clean up all active polling timers (call on unmount). */
        clearAllPolling,

        /** List of assets currently being uploaded or processed. */
        inProgressAssetData: Object.values(inProgressAssets),

        /** Track a replacement asset (from edit/save) in the polling loop until it settles. */
        trackReplacementAsset,

        /** Upload a file as a techline case asset with full lifecycle tracking. */
        uploadAsset,
    };
}
